import { db } from "../firebase";
import { Group, GroupCredentials } from "./types";
import firebase from "firebase/app";
import { createStandaloneToast } from "@chakra-ui/react";
import {
  ERROR_TOAST_DURATION,
  GROUP_COLLECTION_NAME,
  GROUP_CREDENTIALS_COLLECTION_NAME,
  USERS_COLLECTION_NAME,
} from "./constants";
import debug from "debug";

export const createGroup = async (
  group: Omit<Group, "created" | "active" | "gid" | "public" | "members" | "payment_date">,
  credentials: Omit<GroupCredentials, "gid">
) => {
  const toast = createStandaloneToast();

  try {
    const groupRef = db.collection(GROUP_COLLECTION_NAME).doc();
    const groupObj: Group = {
      pid: group.pid,
      slots: group.slots,
      created: firebase.firestore.FieldValue.serverTimestamp(),
      active: false, // Groups are in private by default, they need to be made public manually
      public: true,
      uid: group.uid,
      gid: groupRef.id,
      members: [],
      slotsLeftToBuy: group.slots,
    };
    await groupRef.set(groupObj);

    const credentialsObj: GroupCredentials = {
      gid: groupRef.id,
      account_user: credentials.account_user,
      account_password: credentials.account_password,
    };
    await db.collection(GROUP_CREDENTIALS_COLLECTION_NAME).add(credentialsObj);
    return groupRef.id;
  } catch (error) {
    debug("createGroup")(error.code || error);
    toast({
      title: "Some error occurred!",
      description: "Please try again",
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  }
};

export const updatePhoneNumber = async (uid: string, phoneNumber: string) => {
  const toast = createStandaloneToast();

  try {
    // Assuming the user will always exists
    const userRef = await db.collection(USERS_COLLECTION_NAME).doc(uid);
    const user = await userRef.get();

    if (!user.exists) {
      toast({
        title: "User does not exists",
        description: "Please contact support",
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
      return null;
    }

    await userRef.update({
      phone: phoneNumber,
    });

    return userRef.id;
  } catch (error) {
    debug("updatePhoneNumber")(error.code || error);
    toast({
      title: "Some error occurred!",
      description: "Please try again",
      status: "error",
      duration: 4000,
      isClosable: true,
    });
    return null;
  }
};

export const deleteGroup = async (uid: string, gid: string) => {
  try {
    const deleteGroup = firebase.functions().httpsCallable("deleteGroup");
    const response = await deleteGroup({
      uid,
      gid,
    });
    if (response.data.success) {
      return {
        status: true,
      };
    } else {
      throw new Error();
    }
  } catch (error) {
    const toast = createStandaloneToast();
    toast({
      title: "Error in deleting group",
      description: "Please try again later!",
      status: "error",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
    return {
      result: error,
      status: false,
    };
  }
};

export const updateSlots = async (gid: string, slots: number) => {
  try {
    const groupRef = db.collection(GROUP_COLLECTION_NAME).doc(gid);
    await groupRef.update({
      slots,
    });

    return true;
  } catch (error) {
    debug("updateSlots")(error.code || error);
    return false;
  }
};

export const updateSlotsLeftToBuy = async (gid: string) => {
  try {
    const groupRef = db.collection(GROUP_COLLECTION_NAME).doc(gid);
    await groupRef.update({
      slotsLeftToBuy: ((await groupRef.get()).data()?.slotsLeftToBuy || 0) - 1,
    });

    return true;
  } catch (error) {
    debug("updateSlots")(error.code || error);
    return false;
  }
}